/**
 * Component: VIN Lookup
 * ------------------------------------------------------------------------------
 * VIN Lookup component.
 * - Run with container as parameter: VinLookup('[js-vin-location="header"]').init()
 *
 * @namespace VinLookup
 */
import log from 'salvo-lite/log'

import axios from 'axios'
import convert from 'xml-js'
import * as a11y from '@shopify/theme-a11y'

import cssClasses from '../helpers/cssClasses'
import {getQueryParamsObject, on} from '../helpers/utils'
import bind from '../mixins/bind'

class VinLookup {
    constructor (theme, elem) {
        this._theme = theme
        this._elem = elem
        this._settings = {
            setContainer: '[js-vin="setContainer"]',
            carContainer: '[js-vin="carContainer"]',
            component: '[js-vin="component"]',
            toggleMobile: '[js-vin="toggle-mobile"]',

            searchForm: '[js-vin="searchForm"]',
            searchInput: '[js-vin="searchInput"]',
            searchError: '[js-vin="searchError"]',

            manualLookupForm: '[js-vin="manualLookupForm"]',
            modelDropdown: '[js-vin="modelDropdown"]',
            yearDropdown: '[js-vin="yearDropdown"]',

            inlineSubmit: '[js-vin="inlineSubmit"]',

            ...JSON.parse(elem.getAttribute('data-vin-lookup') || '{}')
        }

        /**
         * DOM node selectors.
         */
        this._setContainer = this._elem.querySelector(this._settings.setContainer)
        this._searchForm = this._setContainer.querySelector(this._settings.searchForm)

        this._nodeSelectors = {
            container: this._elem,
            carContainer: this._elem.querySelector(this._settings.carContainer),
            toggleMobile: this._elem.querySelector(this._settings.toggleMobile),
            component: [...this._setContainer.querySelectorAll(this._settings.component)],

            searchInput: this._searchForm.querySelector(this._settings.searchInput),
            searchError: this._setContainer.querySelector(this._settings.searchError),

            manualLookupForm: this._setContainer.querySelector(this._settings.manualLookupForm),
            modelDropdown: this._setContainer.querySelector(this._settings.modelDropdown),
            yearDropdown: [...this._setContainer.querySelectorAll(this._settings.yearDropdown)],

            inlineSubmit: this._setContainer.querySelector(this._settings.inlineSubmit)
        }

        /**
         * Initialise component bind.
         */
        this._binder = bind(this._nodeSelectors.container)

        log.debug(this.constructor.name, 'Constructed', this)
    }

    /**
     * Initialise component.
     */
    onInit () {
        if (this._binder && this._binder.isSet()) {
            return
        }

        this._setEventBusListeners()
        this._setEventListeners()

        /**
         * Check URL parameters on main header.
         */
        if (this._elem === '[js-vin-location="header"]') {
            this._checkUrlParameters()
        }

        this._binder.set()
    }

    /**
     * Set eventBus listeners.
     */
    _setEventBusListeners () {
        window.Frame.EventBus.listen('Toggle:vinTarget:open', (response) => this._handleVinTrigger(response))

        window.Frame.EventBus.listen('Toggle:vinTarget:close', () => {
            document.body.classList.remove(cssClasses.settingCurrentCar)
            document.body.classList.remove(cssClasses.currentCarView)
            this._setContainer.removeAttribute('data-banner-type')
        })

        if (this._nodeSelectors.inlineSubmit) {
            window.Frame.EventBus.listen('CurrentCar:setCar', () => this._setDefaultState())
        }
    }

    /**
     * Set event listeners.
     */
    _setEventListeners () {
        this._nodeSelectors.component.forEach((element) => {
            on('click', element, () => this._highlightActiveComponent(element))
        })

        if (this._nodeSelectors.searchInput) {
            on('input', this._nodeSelectors.searchInput, () => this._handleSearchInput(this._nodeSelectors.searchInput))
            on('submit', this._searchForm, (event) => this._handleSearchSubmission(event))
        }

        if (this._nodeSelectors.modelDropdown) {
            on('change', this._nodeSelectors.modelDropdown, (event) => this._handleModelDropdownChange(event, this._nodeSelectors.modelDropdown))
            on('submit', this._nodeSelectors.manualLookupForm, (event) => this._handleManualFormSubmission(event))
        }

        if (this._nodeSelectors.inlineSubmit) {
            this._nodeSelectors.yearDropdown.forEach((element) => {
                on('change', element, (event) => this._handleYearDropdownChange(event))
            })

            on('click', this._nodeSelectors.inlineSubmit, () => this._handleInlineSubmit())
        }

        if (this._nodeSelectors.toggleMobile) {
            this._nodeSelectors.toggleMobile.querySelectorAll('input[type=radio]').forEach((input) => {
                input.addEventListener('change', (event) => {
                    let element

                    if (event.target.value === 'reg') {
                        element = event.currentTarget.closest('.vin-lookup__contents').querySelector('.vin-set__component.vin-set__search')
                    } else {
                        element = event.currentTarget.closest('.vin-lookup__contents').querySelector('.vin-set__component.vin-set__manual')
                    }
                    this._highlightActiveComponent(element)
                })
            })
        }
    }

    /**
     * Check URL parameters for ?vin or ?reg.
     * - Defaults to VIN if both are present.
     * - Won't run if current car is already set.
     */
    _checkUrlParameters () {
        if (window.Frame.CurrentCarCookie.isCookieSet()) {
            return
        }

        const urlParameters = getQueryParamsObject()
        const parameterValue = urlParameters.vin || urlParameters.reg || false

        let searchType = 'reg'

        if (Object.keys(urlParameters).includes('vin')) {
            searchType = 'vin'
        }

        if (!parameterValue || parameterValue.length < 7) {
            return
        }

        this._runSearch(parameterValue, searchType)
    }

    /**
     * Handle triggers from lookup, wheel config, ESB, and collection.
     * @param {String} type - Type of event that triggered it.
     */
    _handleVinTrigger (element) {
        this._setDefaultState()
        const type = element.getAttribute('data-trigger-type')

        /**
         * Do nothing if banner is lookup.
         */
        if (type === 'lookup' || type === '') {
            return
        }

        if (type === 'esb' || type === 'sp') {
            this._setContainer.setAttribute('data-banner-type', type)
            this._nodeSelectors.carContainer.setAttribute('data-banner-type', type)

            /**
             * Delay focus until element is showing.
             */
            window.setTimeout(() => {
                a11y.trapFocus(this._nodeSelectors.searchInput)
            }, 0)

        /**
         * If it's not ESB redirect (collection behaviour).
         */
        } else if (window.Frame.CurrentCarCookie.isCookieSet()) {
            document.body.classList.add(cssClasses.disableOverlay)
            this._nodeSelectors.container.classList.add(cssClasses.hidden)

            window.Frame.EventBus.emit('CurrentCar:redirect', type)
        } else {
            this._setContainer.setAttribute('data-banner-type', type)
            this._nodeSelectors.carContainer.setAttribute('data-banner-type', type)
        }
    }

    /**
     * Default state.
     */
    _setDefaultState () {
        this._resetManualDropdowns()
        this._resetSearchInputs()
        this._hideSearchErrorState()

        if (window.innerWidth > 991) {
            this._removeComponentHighlight()
        }

        if (!this._nodeSelectors.inlineSubmit) {
            this._showSetBanner()
        }
    }

    /**
     * Highlight the active component.
     * @param {Object} element - Clicked in object.
     */
    _highlightActiveComponent (element) {
        this._nodeSelectors.component.forEach((item) => {
            if (item === element) {
                item.classList.remove(cssClasses.disabled)
            } else {
                item.classList.add(cssClasses.disabled)
            }
        })
    }

    /**
     * Handle input into search input.
     * - Set search type.
     * - Format it based on number of characters.
     * - Enable form if the correct length.
     * @param {Object} element - Input element.
     */
    _handleSearchInput (element) {
        this._hideSearchErrorState()
        const countryCode = 'gb'
        const searchValue = element.value.replace(/\s/g, '')

        if (searchValue.length < 7) {
            this._setRegSearch()
            this._enableFormSubmission()
        } else if (searchValue.length === 7) {
            element.value = this._setRegFormatting(searchValue, countryCode)
            this._setRegSearch()
            this._enableFormSubmission()
        } else if (searchValue.length === 17) {
            element.value = this._setVinFormatting(searchValue)
            this._setVinSearch()
            this._enableFormSubmission()
        } else {
            element.value = searchValue
            this._disableFormSubmission()
        }
    }

    /**
     * Set search type to VIN.
     */
    _setVinSearch () {
        this._nodeSelectors.searchInput.setAttribute('data-search-type', 'vin')
    }

    /**
     * Set search type to Reg.
     */
    _setRegSearch () {
        this._nodeSelectors.searchInput.setAttribute('data-search-type', 'reg')
    }

    /**
     * Format to match GB or DE registration plates.
     * - The DE registration plates were dropped but left it in case we ever need it.
     * @param {String} searchValue - Search value.
     * @param {String} countryCode - Country code of the registration plate.
     */
    _setRegFormatting (searchValue, countryCode) {
        let formattedValue = ''

        switch (countryCode) {
        case 'gb':
            formattedValue = `${searchValue.slice(0, 4)} ${searchValue.slice(4)}`
            break

        case 'de':
            formattedValue = `${searchValue.slice(0, 2)} ${searchValue.slice(2, 4)} ${searchValue.slice(4)}`
            break

        default:
            formattedValue = `${searchValue.slice(0, 4)} ${searchValue.slice(4)}`
        }

        return formattedValue
    }

    /**
     * Format to match VIN numbers.
     * @param {String} searchValue - Search value.
     */
    _setVinFormatting (searchValue) {
        const formattedValue = [searchValue.slice(0, 3), searchValue.slice(3, 8), searchValue.slice(8, 11), searchValue.slice(11)].join(' ')

        return formattedValue
    }

    /**
     * Enable form button.
     */
    _enableFormSubmission () {
        this._searchForm.querySelector('button').classList.remove(cssClasses.disabled)
        this._searchForm.querySelector('button').disabled = false

        if (this._nodeSelectors.inlineSubmit) {
            this._setInlineSubmit('search')
        }
    }

    /**
     * Enable form button.
     */
    _disableFormSubmission () {
        this._searchForm.querySelector('button').classList.add(cssClasses.disabled)
        this._searchForm.querySelector('button').disabled = true

        if (this._nodeSelectors.inlineSubmit) {
            this._revertInlineSubmit('search')
        }
    }

    /**
     * Handles VIN lookup search form submission.
     * @param {Event} event - Submission event.
     */
    _handleSearchSubmission (event) {
        if (event) {
            event.preventDefault()
        }

        this._setLoadingState()
        this._hideSearchErrorState()

        const searchValue = this._nodeSelectors.searchInput.value.replace(/\s/g, '')

        if (searchValue.length < 7) {
            this._unsetLoadingState()
            this._handleSearchErrorState('unrecognised')
            return
        }

        const searchType = this._nodeSelectors.searchInput.getAttribute('data-search-type')

        this._runSearch(searchValue, searchType)
    }

    /**
     * Runs the search and handles the events.
     * @param {String} searchValue - Search value.
     * @param {String} searchType - Either `reg` or `vin`.
     */
    _runSearch (searchValue, searchType) {
        this._getAjaxResponse(searchValue, searchType)
            .then((response) => {
                this._unsetLoadingState()

                if (response) {
                    const currentCar = this._convertResponseToCurrentCar(response)
                    window.Frame.EventBus.emit('CurrentCar:setCar', currentCar)
                    window.Frame.EventBus.emit('DataLayer:carRegistration', currentCar)
                    window.Frame.EventBus.emit('Overlay:vinTarget:hide')
                    return
                }

                throw new Error('Response not found')
            })
            .catch((error) => {
                this._unsetLoadingState()
                this._handleSearchErrorState(error)
            })
    }

    /**
     * Submit the VIN search.
     * @param {String} searchValue - Search value.
     * @param {String} searchType - Either `reg` or `vin`.
     */
    _getAjaxResponse (searchValue, searchType) {
        let searchUrl = ''

        switch (searchType) {
        case 'reg':
            searchUrl = `https://live.eu.oec.ford.eastsideapps.io/api/v1/reg/${searchValue}`
            break
        case 'vin':
            searchUrl = `https://live.eu.oec.ford.eastsideapps.io/api/v1/vin/${searchValue}`
            break
        }

        return new Promise((resolve, reject) => {
            axios.get(searchUrl)
                .then((response) => resolve(response))
                .catch((error) => reject(error))
        })
    }

    /**
     * Shows loading icon.
     */
    _setLoadingState () {
        this._searchForm.querySelector('button').classList.add(cssClasses.loading)

        if (this._nodeSelectors.inlineSubmit) {
            this._nodeSelectors.inlineSubmit.classList.add(cssClasses.loading)
        }
    }

    /**
     * Hides loading icon.
     */
    _unsetLoadingState () {
        this._searchForm.querySelector('button').classList.remove(cssClasses.loading)

        if (this._nodeSelectors.inlineSubmit) {
            this._nodeSelectors.inlineSubmit.classList.remove(cssClasses.loading)
        }
    }

    /**
     * Convert the JSON response into the current car object.
     * @param {JSON} response - JSON VIN results.
     * @returns {Object} - Current car object.
     */
    _convertResponseToCurrentCar (response) {
        let lookupResults = convert.xml2json(response.data, {compact: true, spaces: 0})
        lookupResults = JSON.parse(lookupResults)

        if (lookupResults.vinDecodeResults) {
            lookupResults = lookupResults.vinDecodeResults.vinDecodeResult
        } else if (lookupResults.regnDecodeResults) {
            lookupResults = lookupResults.regnDecodeResults.regnDecodeResult
        }

        const currentCar = {}
        const yearRegex = /(\s+[0-9-]{5,9})/g

        let vehicleLine = this._individualResultTextFormatter(lookupResults.VehicleLine._text)
        vehicleLine = vehicleLine.replace(' -', '-').replace(' - ', '-')

        /**
         * Test to see if the vehicleLine has a year.
         */
        if (vehicleLine.match(yearRegex)) {
        /**
         * Can't just split on the space in case a model name contains a space.
         * So the below uses Regex to recognise date format then adds character to split on.
         */
            vehicleLine = vehicleLine.replace(yearRegex, '||$1')
            vehicleLine = vehicleLine.split('||')

            currentCar.model = vehicleLine[0]
            currentCar.year = vehicleLine[1].replace(/\s+/g, '')

        /**
         * Else only use the model, this is because some results have no year.
         */
        } else {
            currentCar.model = vehicleLine
        }

        /**
         * Colour.
         */
        currentCar.colour = this._individualResultTextFormatter(lookupResults.ExteriorPaint._text)

        /**
         * Service plan data.
         */
        currentCar.emissionCode = this._individualResultCodeFormatter(lookupResults.EmissionRequirements._text)
        currentCar.engineCode = this._individualResultCodeFormatter(lookupResults.EngineType._text).substring(2, 4)
        currentCar.productionDate = lookupResults.ProductionDate._text
        if (currentCar.registration) { currentCar.registration = lookupResults.RegistrationNumber._text }
        currentCar.vehicleCode = this._individualResultCodeFormatter(lookupResults.VehicleLine._text)
        currentCar.vinNumber = lookupResults.VIN._text
        currentCar.transmissionCode = this._individualResultCodeFormatter(lookupResults.Transmission._text)

        /**
         * Current car method.
         */
        currentCar.method = 'lookup'

        return currentCar
    }

    /**
     * Removes fluff from outputs text.
     * @param {String} value - Original response value.
     * @returns {String} - Cleaned up value.
     */
    _individualResultTextFormatter (value) {
        let newValue = value.split('][')
        newValue = newValue[1]
        newValue = newValue.slice(0, -1)

        return newValue
    }

    /**
     * Removes fluff and outputs code.
     * @param {String} value - Original response value.
     * @returns {String} - Cleaned up value.
     */
    _individualResultCodeFormatter (value) {
        let newValue = value.split('][')
        newValue = newValue[0]
        newValue = newValue.substr(1)

        return newValue
    }

    /**
     * Handle error codes message.
     * @param {String} error - Error response.
     */
    _handleSearchErrorState (error) {
        this._nodeSelectors.searchError.classList.add(cssClasses.active)
        const errorString = String(error)

        if (errorString.includes('400')) {
            this._nodeSelectors.searchError.innerHTML = window.theme.strings.vinLookup.errors.error_400
        } else if (errorString.includes('401')) {
            this._nodeSelectors.searchError.innerHTML = window.theme.strings.vinLookup.errors.error_401
        } else if (errorString.includes('404')) {
            this._nodeSelectors.searchError.innerHTML = window.theme.strings.vinLookup.errors.error_404
        } else if (errorString.includes('500')) {
            this._nodeSelectors.searchError.innerHTML = window.theme.strings.vinLookup.errors.error_500
        } else if (errorString.includes('unrecognised')) {
            this._nodeSelectors.searchError.innerHTML = window.theme.strings.vinLookup.errors.unrecognised_vehicle
        } else {
            this._nodeSelectors.searchError.innerHTML = window.theme.strings.vinLookup.errors.unknown_error
        }

        throw new Error(errorString)
    }

    /**
     * Hide the error message and set to nothing.
     */
    _hideSearchErrorState () {
        this._nodeSelectors.searchError.classList.remove(cssClasses.active)
        this._nodeSelectors.searchError.innerHTML = ''
    }

    /**
     * Handle the model dropdown.
     * @param {Event} event - Change event.
     * @param {Object} element - Dropdown where the event happens.
     */
    _handleModelDropdownChange (event, element) {
        event.preventDefault()

        const value = element.value
        this._revealYearDropdown(value)
        this._activateManualSubmitButton(value)

        if (this._nodeSelectors.inlineSubmit) {
            this._hideSearchErrorState()

            if (event.target.value === 'default') {
                this._revertInlineSubmit('manual')
                return
            }

            this._setInlineSubmit('manual')
        }
    }

    /**
     * Show the correct year dropdown based on model dropdown choice.
     * @param {String} value - Model value in handle format.
     */
    _revealYearDropdown (value) {
        this._nodeSelectors.yearDropdown.forEach((item) => {
            const model = item.getAttribute('data-model')

            if (model === value) {
                item.classList.add(cssClasses.active)
            } else {
                item.classList.remove(cssClasses.active)
            }
        })
    }

    /**
     * Activate the submit button if a model has been selected.
     * @param {String} value - Model value in handle format.
     */
    _activateManualSubmitButton (value) {
        if (value === 'default') {
            this._nodeSelectors.manualLookupForm.querySelector('button').disabled = true
        } else {
            this._nodeSelectors.manualLookupForm.querySelector('button').disabled = false
        }
    }

    /**
     * Build manual form values for Current Car.
     * @param {Event} event - Form submission event.
     * @returns {Object} - Emits event to event bus with modelYear object.
     */
    _handleManualFormSubmission (event) {
        if (event) {
            event.preventDefault()
        }

        const currentCar = {
            model: this._nodeSelectors.modelDropdown.value,
            year: this._yearDropdownValue(),
            method: 'manual'
        }

        window.Frame.EventBus.emit('CurrentCar:setCar', currentCar)
        window.Frame.EventBus.emit('DataLayer:carRegistration', currentCar)
        window.Frame.EventBus.emit('Overlay:vinTarget:hide')
    }

    /**
     * Retrieve active year dropdown value.
     */
    _yearDropdownValue () {
        let selectedYearValue = ''

        this._nodeSelectors.yearDropdown.forEach((item) => {
            if (!item.classList.contains(cssClasses.active)) {
                return
            }

            selectedYearValue = item.value
        })

        return selectedYearValue
    }

    /**
     * Reset the manual lookup dropdowns.
     */
    _resetManualDropdowns () {
        /**
         * Don't reset model dropdown if its value is hard-coded.
         * Used on model landing page's inline VIN lookup.
         */
        if (!this._nodeSelectors.modelDropdown || this._nodeSelectors.modelDropdown.getAttribute('data-model') === 'set') {
            return
        }

        this._nodeSelectors.modelDropdown.querySelector('[value="default"]').selected = true

        this._nodeSelectors.yearDropdown.forEach((item) => {
            if (item.getAttribute('data-model') === 'default') {
                item.classList.add(cssClasses.active)
                return
            }

            item.classList.remove(cssClasses.active)
        })
    }

    /**
     * Reset the search inputs to blank.
     */
    _resetSearchInputs () {
        this._nodeSelectors.searchInput.setAttribute('data-search-type', '')
        this._nodeSelectors.searchInput.value = ''

        this._disableFormSubmission()
    }

    /**
     * Switches display of car to set banner in VIN lookup
     */
    _showSetBanner () {
        document.body.classList.add(cssClasses.settingCurrentCar)
    }

    /**
     * Remove the disable from components.
     */
    _removeComponentHighlight () {
        this._nodeSelectors.component.forEach((item) => {
            item.classList.remove(cssClasses.disabled)
        })
    }

    /**
     * Handle year change event.
     * - Used to activate inline form button.
     * @param {Event} event - The change event.
     */
    _handleYearDropdownChange (event) {
        this._hideSearchErrorState()

        if (event.target.value === 'default') {
            this._revertInlineSubmit('manual')
            return
        }

        this._setInlineSubmit('manual')
    }

    /**
     * Set inline submit state.
     * @param {String} form - The type of form to set to active.
     */
    _setInlineSubmit (form) {
        this._nodeSelectors.inlineSubmit.removeAttribute('disabled')
        this._nodeSelectors.inlineSubmit.setAttribute('data-last', form)
        this._nodeSelectors.inlineSubmit.setAttribute(`data-${form}`, true)
    }

    /**
     * Revert inline submit state.
     * @param {String} form - The type of form to revert.
     */
    _revertInlineSubmit (form) {
        this._nodeSelectors.inlineSubmit.setAttribute(`data-${form}`, false)

        const search = this._nodeSelectors.inlineSubmit.getAttribute('data-search')
        const manual = this._nodeSelectors.inlineSubmit.getAttribute('data-manual')

        if (search !== 'true' && manual !== 'true') {
            this._nodeSelectors.inlineSubmit.setAttribute('disabled', true)
            this._nodeSelectors.inlineSubmit.setAttribute('data-last', '')
        } else if (search === 'true') {
            this._nodeSelectors.inlineSubmit.setAttribute('data-last', 'search')
        } else if (manual === 'true') {
            this._nodeSelectors.inlineSubmit.setAttribute('data-last', 'manual')
        }
    }

    /**
     * Handle inline form submission.
     * - Checks for content in each part.
     * - Triggers that form's submit button.
     */
    _handleInlineSubmit () {
        const form = this._nodeSelectors.inlineSubmit.getAttribute('data-last')

        switch (form) {
        case 'search':
            this._handleSearchSubmission()
            break

        case 'manual':
            this._handleManualFormSubmission()
            break
        }
    }

    /**
     * Expose public interface.
     */
//     return Object.freeze({
//         init,
//     });
}

export default VinLookup



// WEBPACK FOOTER //
// ./src/resources/scripts/components/VinLookup.js